import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/providers/tenant-context';
export default function PrivacyPage() {
  const { tenant, isWhitelabel } = useTenant();

  return (
    <div className="flex flex-col min-h-screen bg-background relative overflow-x-hidden transition-colors duration-300 font-sans">
      {/* Background Ambience */}
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />
      <div className="fixed inset-0 w-full h-full -z-10 opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] pointer-events-none" />

      {/* Navigation */}
      <header className="fixed top-0 w-full z-50 border-b border-border/10 bg-background/60 backdrop-blur-xl">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <Link className="flex items-center gap-2 group" to="/">
            {isWhitelabel && tenant?.logoWithName ? (
              <img
                src={tenant.logoWithName}
                alt={tenant.name}
                className="h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <>
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-10 h-10 object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <span className="font-bold text-2xl tracking-tighter text-foreground">
                  {isWhitelabel ? tenant?.name : 'Milora'}
                </span>
              </>
            )}
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" className="rounded-full gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-32 pb-20">
        <div className="container px-6 mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-12"
          >
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary uppercase tracking-wider">
                <Shield className="h-3 w-3" />
                <span>Privacy First</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight text-foreground">
                Privacy Policy
              </h1>
              <p className="text-muted-foreground text-lg">
                Last updated:{' '}
                {new Date().toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>

            <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">1. Operator</h2>
                <p className="text-muted-foreground leading-relaxed">
                  This Privacy Policy applies to the{' '}
                  {isWhitelabel && tenant?.name ? tenant.name : 'Milora'} platform operated by{' '}
                  <a
                    href="https://simpliumtechnologies.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Simplium Technologies LLP
                  </a>
                  . References to &quot;we&quot;, &quot;us&quot;, or &quot;our&quot; mean Simplium
                  Technologies LLP.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">2. Information We Collect</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We collect information you provide directly to us when you create an account,
                  update your profile, join a community, or communicate with us. This may include
                  your name, email address, phone number, and any other information you choose to
                  provide.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  3. How We Use Your Information
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We use the information we collect to provide, maintain, and improve our services,
                  to facilitate community interactions, to send you technical notices and support
                  messages, and to respond to your comments and questions.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">4. Information Sharing</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We do not share your personal information with third parties except as described
                  in this policy. Information you share within a community (like your profile
                  details or posts) is visible to other members of that community according to the
                  community&apos;s privacy settings.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">5. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We take reasonable measures to help protect information about you from loss,
                  theft, misuse and unauthorized access, disclosure, alteration and destruction.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">6. Your Choices</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You may update or correct your account information at any time by logging into
                  your account or by contacting us. You can also request the deletion of your
                  account through our{' '}
                  <Link to="/contact" className="text-primary hover:underline">
                    contact form
                  </Link>
                  .
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">7. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact Simplium
                  Technologies LLP at support@milora.com.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border/40 bg-muted/20">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2">
            {isWhitelabel && tenant?.logoWithName ? (
              <img
                src={tenant.logoWithName}
                alt={tenant.name}
                className="h-8 w-auto object-contain grayscale opacity-80"
              />
            ) : (
              <>
                <img
                  src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                  alt={isWhitelabel ? tenant?.name : 'Milora'}
                  className="w-8 h-8 object-contain grayscale opacity-80"
                />
                <span className="font-bold text-lg text-muted-foreground">
                  {isWhitelabel ? tenant?.name : 'Milora'}
                </span>
              </>
            )}
          </div>
          <p className="text-sm text-muted-foreground/60">
            © {new Date().getFullYear()} Simplium Technologies LLP
          </p>
        </div>
      </footer>
    </div>
  );
}
